import { RoasteryAuthProvider } from "@roastery/auth";
import { I18nextProvider, createI18n, isSupportedLanguage } from "@roastery/i18n";
import { Toaster } from "@roastery/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary, RouteErrorScreen, initErrorTracking } from "@roastery/observability";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { routeTree } from "./routeTree.gen";
import { runtimeConfig } from "./runtime-config";
import { addressOrigin, initTenantAddress } from "./tenant-address";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 401, 403 or 402 will not become a 200 by asking again - the last one is the plan
      // refusing (EP-15), which no amount of retrying changes either.
      retry: (failureCount, error) => failureCount < 2 && !isAuthError(error),
    },
  },
});

function isAuthError(error: unknown): boolean {
  const status = (error as { status?: number } | undefined)?.status;
  return status === 401 || status === 403 || status === 402;
}

// Awaited, because the dictionary is a chunk of its own since US-37.8: rendering first and
// translating afterwards would paint the keys.
const i18n = await createI18n(
  isSupportedLanguage(runtimeConfig.defaultLocale) ? runtimeConfig.defaultLocale : "pl",
);

// The server answers in the language we ask for (US-23.4) - a product's description, a validation
// message, a problem detail. A cached answer is keyed by method and path, not by the header, so a
// change of language makes every one of them stale.
i18n.on("languageChanged", () => void queryClient.invalidateQueries());

type RouteNode = { options?: { path?: string }; children?: RouteNode[] };

/**
 * The application's own first path segments, read off the route tree rather than written out, so
 * adding a screen can never quietly turn its name into a roastery slug (US-10.5).
 *
 * <p>Recursive since US-24.4. A pathless layout — `_authed`, which holds everything behind the sign-in
 * — contributes no segment of its own, so a walk of the root's direct children alone would come back
 * with `t` and nothing else, and `portal.localhost/orders` would start looking like a roastery called
 * "orders".
 *
 * <p><strong>`options.path`, not `path`</strong> (US-37.2). A route object gets its own `path` when
 * the router is built, and this runs before that — so every node read back `undefined`, the set came
 * back **empty**, and the safety net this function is had never held anything: `portal.localhost/t/x`
 * resolved to a roastery called "t" and asked the API for its branding (T-08 of the 2026-09-15
 * audit). The paths are on `options` from the moment the tree is imported.
 */
function routeSegments(): ReadonlySet<string> {
  const segments = new Set<string>();
  const walk = (nodes: RouteNode[]) => {
    for (const node of nodes) {
      const first = String(node.options?.path ?? "")
        .split("/")
        .filter(Boolean)[0];
      if (first) {
        segments.add(first);
      } else {
        walk(node.children ?? []);
      }
    }
  };
  walk((routeTree as RouteNode).children ?? []);
  return segments;
}

// Every route hangs off the roastery's address (US-10.5), so `<Link to="/orders">` keeps working
// whether the portal is served from a subdomain or from /{slug}.
const address = initTenantAddress(routeSegments());
const errorLabels = () => ({
  title: i18n.t("errors.boundary.title"),
  description: i18n.t("errors.boundary.description"),
  eventId: i18n.t("errors.boundary.eventId"),
  reload: i18n.t("errors.boundary.reload"),
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  basepath: address.basepath,
  // A screen that throws lands here, not in the boundary around the router (US-18.9): the router wraps
  // every route in its own. Same screen, same event in GlitchTip.
  defaultErrorComponent: ({ error }) => <RouteErrorScreen error={error} labels={errorLabels} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("#root element is missing from index.html");
}

// Errors as events (US-18.9): before the first render, so a throw during it is the first event.
initErrorTracking({
  dsn: runtimeConfig.sentryDsn,
  environment: runtimeConfig.environment,
  release: runtimeConfig.release,
  app: "customer-portal",
});

createRoot(rootElement).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <RoasteryAuthProvider
          authority={runtimeConfig.oidcAuthority}
          clientId={runtimeConfig.oidcClientId}
          apiBaseUrl={runtimeConfig.apiUrl}
          redirectUri={addressOrigin(address)}
          // Signing out lands on a page that says so, not on the login screen just left (US-23.6).
          postLogoutRedirectUri={`${addressOrigin(address)}/signed-out`}
          // The language the screens are read in, so the server answers in it (US-23.4): a product's
          // description, a validation message and a problem detail all come from Accept-Language.
          getLanguage={() => i18n.resolvedLanguage}
          // ...and so does the sign-in screen (US-23.1), which is a different server and a different
          // mechanism: `ui_locales` on the authorization request.
          uiLocales={i18n.resolvedLanguage}
        >
          <ErrorBoundary labels={errorLabels}>
            <RouterProvider router={router} />
          </ErrorBoundary>
          <Toaster />
        </RoasteryAuthProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
